// VoiceRapportagePage.jsx - AI Rapportage voor dagrapportages (alleen tekst)
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  FileText,
  Loader2,
  CheckCircle,
  Send,
  Sparkles,
  Trash2,
  PenLine,
  X,
  Copy,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from './shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceRapportagePage = ({ onBack, user, selectedLocation }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDienst, setSelectedDienst] = useState(null);
  const [handmatigeTekst, setHandmatigeTekst] = useState('');
  const [rapportage, setRapportage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [rapportages, setRapportages] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [viewingRapportage, setViewingRapportage] = useState(null);
  
  const locationSlug = selectedLocation || user?.selected_location || 'groep-zwaluw';

  const DIENSTEN = [
    {
      id: 'volledig',
      naam: 'Ochtend/Middag/Avond/Nacht',
      tijden: '24-uurs',
      icon: '🌞',
      beschrijving: '1-op-1 begeleiding'
    },
    {
      id: 'dagvolledig',
      naam: 'Ochtend/Middag/Avond',
      tijden: '08:00 - 22:00',
      icon: '☀️',
      beschrijving: 'Volledige dagdienst'
    },
    {
      id: 'dag',
      naam: 'Ochtend/Middag',
      tijden: '08:00 - 15:00',
      icon: '🌤️',
      beschrijving: 'Dagdienst'
    },
    {
      id: 'nacht',
      naam: 'Avond/Nacht',
      tijden: '15:00 - 08:00',
      icon: '🌙',
      beschrijving: 'Avond- en nachtdienst'
    }
  ];

  const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/voice/clients/${locationSlug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Fout bij ophalen clients:', error);
      setClients([]);
    }
  }, [locationSlug]);

  const fetchRapportages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const vandaag = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/voice/rapportages?locatie=${locationSlug}&datum=${vandaag}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRapportages(response.data.rapportages || []);
    } catch (error) {
      console.error('Fout bij ophalen rapportages:', error);
    }
  }, [locationSlug]);

  useEffect(() => {
    fetchClients();
    fetchRapportages();
  }, [fetchClients, fetchRapportages]);

  // Wanneer een client wordt geselecteerd
  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  // Genereer rapportage uit handmatig getypte tekst
  const generateFromText = async () => {
    if (!selectedClient || !selectedDienst) {
      toast.error('Selecteer eerst een cliënt en dienst');
      return;
    }
    if (!handmatigeTekst.trim()) {
      toast.error('Voer eerst je observaties in');
      return;
    }

    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      
      toast.info('📝 Bezig met rapportage genereren...');
      const rapportageResponse = await axios.post(`${API_URL}/api/voice/generate-rapportage`, {
        client_voornaam: selectedClient.voornaam,
        locatie: locationSlug,
        transcriptie: handmatigeTekst,
        dienst: selectedDienst.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRapportage(rapportageResponse.data.rapportage);
      
      const newRap = {
        client_voornaam: selectedClient.voornaam,
        client_initialen: rapportageResponse.data.client_initialen,
        rapportage: rapportageResponse.data.rapportage,
        rapportage_id: rapportageResponse.data.rapportage_id,
        dienst: selectedDienst.naam
      };
      setRapportages(prev => [newRap, ...prev]);
      setHandmatigeTekst('');
      
      toast.success(`✅ Rapportage voor ${selectedClient.initialen} gegenereerd!`);
      
    } catch (error) {
      console.error('Verwerking mislukt:', error);
      toast.error('Verwerking mislukt: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const sendAllRapportages = async () => {
    if (rapportages.length === 0) {
      toast.error('Geen rapportages om te versturen');
      return;
    }

    setIsProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const vandaag = new Date().toLocaleDateString('nl-NL');
      
      await axios.post(`${API_URL}/api/voice/send-rapportages`, {
        locatie: locationSlug,
        rapportages: rapportages,
        datum: vandaag
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const emailAdres = locationSlug === 'groep-adelaars' ? 'teamadelaars@zorg-vleugel.nl' : 'teamzwaluw@zorg-vleugel.nl';
      toast.success(`📧 Rapportages verzonden naar ${emailAdres}!`);
      setShowSendModal(false);
      
    } catch (error) {
      toast.error('Versturen mislukt: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteRapportage = async (rapportageId, index) => {
    if (!window.confirm('Weet je zeker dat je deze rapportage wilt verwijderen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      if (rapportageId) {
        await axios.delete(`${API_URL}/api/voice/rapportages/${rapportageId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Verwijder uit lokale state
      setRapportages(prev => prev.filter((_, i) => i !== index));
      toast.success('Rapportage verwijderd');
      
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      toast.error('Verwijderen mislukt: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-purple-50/30" data-testid="voice-rapportage-page">
      <PageHeader 
        title="Rapportage tool" 
        subtitle="Slimme Dagrapportages" 
        onBack={onBack} 
        icon={PenLine} 
        iconGradient="from-violet-500 to-purple-600" 
      />

      <div className="px-4 py-6 pb-32">
        {/* Info banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">Rapportage tool</h3>
              <p className="text-sm text-white/80">Typ je notities, de tool maakt de rapportage</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-white/90">
            Selecteer een cliënt, kies de dienst, en typ je observaties. De tool corrigeert spelfouten en maakt een professionele rapportage.
          </p>
        </div>

        {/* Client selectie */}
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">1. Selecteer Cliënt</h2>
          <div className="grid grid-cols-3 gap-2">
            {clients.map((client) => (
              <button
                key={client.voornaam}
                onClick={() => handleClientSelect(client)}
                className={`p-3 rounded-xl text-center transition-all ${
                  selectedClient?.voornaam === client.voornaam
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300'
                }`}
              >
                <span className="font-semibold text-sm">{client.voornaam}</span>
                <p className={`text-xs mt-0.5 ${selectedClient?.voornaam === client.voornaam ? 'text-white/80' : 'text-slate-400'}`}>
                  {client.initialen}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Dienst selectie */}
        {selectedClient && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">2. Selecteer Dienst</h2>
            <div className="grid grid-cols-2 gap-3">
              {DIENSTEN.map((dienst) => (
                <button
                  key={dienst.id}
                  onClick={() => setSelectedDienst(dienst)}
                  className={`p-4 rounded-2xl text-left transition-all ${
                    selectedDienst?.id === dienst.id
                      ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg'
                      : 'bg-white border border-slate-200 text-slate-700 hover:border-violet-300'
                  }`}
                >
                  <span className="text-2xl block mb-2">{dienst.icon}</span>
                  <h3 className={`font-semibold text-sm ${selectedDienst?.id === dienst.id ? 'text-white' : 'text-slate-800'}`}>
                    {dienst.naam}
                  </h3>
                  <p className={`text-xs mt-1 ${selectedDienst?.id === dienst.id ? 'text-white/80' : 'text-slate-500'}`}>
                    {dienst.tijden}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Handmatig typen sectie - nu altijd zichtbaar na dienst selectie */}
        {selectedClient && selectedDienst && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">3. Typ Je Observaties</h2>
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <p className="text-sm text-slate-500 mb-3">
                Rapportage voor <span className="font-semibold text-violet-600">{selectedClient.initialen}</span>
                <span className="text-xs text-slate-400 ml-2">
                  {selectedDienst.icon} {selectedDienst.naam}
                </span>
              </p>
              
              <textarea
                value={handmatigeTekst}
                onChange={(e) => setHandmatigeTekst(e.target.value)}
                placeholder="Typ hier je observaties over de dienst. Beschrijf wat er is gebeurd, hoe de cliënt zich gedroeg, wat je hebt gedaan als begeleider, etc. De AI corrigeert automatisch spelfouten."
                className="w-full h-40 p-3 rounded-xl border border-slate-200 text-slate-700 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                disabled={isProcessing}
                data-testid="observaties-textarea"
              />
              
              <button
                onClick={generateFromText}
                disabled={isProcessing || !handmatigeTekst.trim()}
                className={`w-full mt-3 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                  isProcessing || !handmatigeTekst.trim()
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700'
                }`}
                data-testid="generate-rapportage-btn"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Bezig met genereren...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Genereer Rapportage
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Gegenereerde rapportage */}
        {rapportage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Gegenereerde Rapportage</h2>
            <div className="p-4 rounded-2xl bg-white border border-violet-200 shadow-sm">
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{rapportage}</pre>
            </div>
          </motion.div>
        )}

        {/* Verzamelde rapportages */}
        {rapportages.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
                Rapportages vandaag ({rapportages.length})
              </h2>
              <button
                onClick={() => setShowSendModal(true)}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-medium flex items-center gap-2 shadow-lg"
                data-testid="send-all-btn"
              >
                <Send className="w-4 h-4" />
                Verstuur Alles
              </button>
            </div>
            
            <div className="space-y-2">
              {rapportages.map((rap, idx) => (
                <button
                  key={rap.id || `rap-${rap.client_voornaam}-${rap.datum}-${idx}`}
                  onClick={() => setViewingRapportage(rap)}
                  className="w-full p-3 rounded-xl bg-white border border-slate-200 flex items-center justify-between hover:border-violet-300 hover:bg-violet-50/50 transition-all active:scale-[0.99]"
                  data-testid={`view-rapportage-${rap.id || idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-800">{rap.client_initialen || rap.client_voornaam}</p>
                      <p className="text-xs text-slate-500">Tik om te lezen</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-violet-500" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRapportage(rap.rapportage_id || rap.id, idx);
                      }}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-rapportage-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Verstuur modal */}
        <AnimatePresence>
          {showSendModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowSendModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-3xl w-full max-w-sm p-6"
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800 mb-2">Rapportages Versturen</h2>
                  <p className="text-slate-600 mb-6">
                    {rapportages.length} rapportage(s) worden verstuurd naar:<br/>
                    <span className="font-semibold text-violet-600">
                      {locationSlug === 'groep-adelaars' ? 'teamadelaars@zorg-vleugel.nl' : 'teamzwaluw@zorg-vleugel.nl'}
                    </span>
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowSendModal(false)}
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={sendAllRapportages}
                      disabled={isProcessing}
                      className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium flex items-center justify-center gap-2"
                    >
                      {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      Versturen
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rapportage bekijken modal */}
        <AnimatePresence>
          {viewingRapportage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
              onClick={() => setViewingRapportage(null)}
            >
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-800">
                        {viewingRapportage.client_initialen || viewingRapportage.client_voornaam}
                      </h2>
                      <p className="text-xs text-slate-500">
                        {viewingRapportage.dienst === 'dag' ? 'Dagdienst' : 'Nachtdienst'} • {new Date(viewingRapportage.aangemaakt_op).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewingRapportage(null)}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="prose prose-sm max-w-none">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {viewingRapportage.rapportage}
                    </pre>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 flex gap-3">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(viewingRapportage.rapportage);
                      toast.success('Rapportage gekopieerd!');
                    }}
                    className="flex-1 px-4 py-3 rounded-xl bg-violet-100 text-violet-700 font-medium flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Kopiëren
                  </button>
                  <button
                    onClick={() => setViewingRapportage(null)}
                    className="flex-1 px-4 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
                  >
                    Sluiten
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VoiceRapportagePage;
